import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import PublicShell, { usePublicSettings } from "./PublicShell";
import client from "@/lib/api";
import { getDecoderRefs, refLabel } from "@/lib/decoderRefs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Decoder() {
  const { t, i18n } = useTranslation();
  const settings = usePublicSettings();
  const [vin, setVin] = useState("");
  const [state, setState] = useState("idle");
  const [result, setResult] = useState(null);
  const [refs, setRefs] = useState({});

  useEffect(() => { getDecoderRefs().then(setRefs); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const v = vin.trim();
    if (!v) return;
    setState("loading");
    try {
      const { data } = await client.get(`/decode/${encodeURIComponent(v)}`, { params: { lang: i18n.language } });
      if (data.found) { setResult(data); setState("found"); }
      else setState("notfound");
    } catch { setState("notfound"); }
  };

  const unit = (k) => t(`decoder.units.${k}`);

  const yearsStr = (r) => {
    const f = r.year_from, tt = r.year_to;
    if (f && tt && tt !== f) return `${f}–${tt}`;
    if (f) return `${f}`;
    return "";
  };
  const powerStr = (r) => {
    if (!r.power_hp) return "";
    let s = `${r.power_hp} ${unit("hp")}`;
    if (r.power_kw) s += ` (${r.power_kw} ${unit("kw")})`;
    return s;
  };
  const volumeStr = (r) => (r.engine_volume ? `${r.engine_volume} ${unit("liter")}` : "");

  const rows = result
    ? [
        [t("decoder.brand"), result.brand],
        [t("decoder.model"), result.model],
        [t("decoder.modification"), result.modification],
        [t("decoder.fuel_type"), refLabel(refs, "fuel_type", result.fuel_type, i18n.language)],
        [t("decoder.engine_type"), refLabel(refs, "engine_type", result.engine_type, i18n.language)],
        [t("decoder.engine_volume"), volumeStr(result)],
        [t("decoder.cylinders"), result.cylinders],
        [t("decoder.power"), powerStr(result)],
        [t("decoder.drive"), refLabel(refs, "drive", result.drive, i18n.language)],
        [t("decoder.body_style"), refLabel(refs, "body_style", result.body_style, i18n.language)],
        [t("decoder.years"), yearsStr(result)],
      ].filter(([, v]) => v)
    : [];

  return (
    <PublicShell settings={settings}>
      <div className="mx-auto max-w-2xl px-6 py-16 sm:py-24">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-5">{t("decoder.badge")}</div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter leading-[1.05] mb-4">{t("decoder.title")}</h1>
        <p className="text-neutral-600 text-base mb-10 max-w-lg">{t("decoder.subtitle")}</p>

        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input data-testid="decoder-input" value={vin} onChange={(e) => setVin(e.target.value.toUpperCase())} placeholder={t("decoder.placeholder")} className="h-14 pl-10 text-base rounded-lg border-neutral-300 tracking-wider" autoFocus />
          </div>
          <Button type="submit" data-testid="decoder-submit" disabled={state === "loading"} className="h-12 px-8 rounded-lg font-semibold active:scale-[0.98] transition-transform" style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>
            {state === "loading" ? t("decoder.searching") : t("decoder.submit")}
          </Button>
        </form>

        {state === "notfound" && (
          <div className="mt-8 border border-neutral-200 rounded-lg p-6 bg-neutral-50" data-testid="decoder-notfound">
            <p className="font-medium">{t("decoder.notfoundTitle")}</p>
            <p className="text-sm text-neutral-600 mt-1">{t("decoder.notfoundText")}</p>
          </div>
        )}

        {state === "found" && result && (
          <div className="mt-8 border border-neutral-200 rounded-lg overflow-hidden" data-testid="decoder-result">
            <div className="px-6 py-4 border-b border-neutral-200 bg-neutral-50">
              <div className="text-xs uppercase tracking-widest text-neutral-500">{t("decoder.fin")}</div>
              <div className="text-lg font-semibold tracking-wider">{result.vin}</div>
            </div>
            <div className="divide-y divide-neutral-100">
              {rows.map(([k, v]) => (
                <div key={k} className="flex px-6 py-3 text-sm" data-testid={`decoder-row`}>
                  <div className="w-2/5 text-neutral-500">{k}</div>
                  <div className="w-3/5 font-medium">{v}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PublicShell>
  );
}
